import "reflect-metadata";

import type ISoftwareRepository from "../ISoftwareRepository";
import type {
  Filters,
  ProductPlan,
  ProductPlanListDTO,
  SoftwareCreateRq,
  Software,
  UsageMetric,
} from "../software.types";
import type { AxiosRequestConfig } from "axios";
import { injectable } from "inversify";
import qs from "qs";
import API from "@repositories/Axios";
import type { Page } from "@type/Page";
import type { SoftwareSimpleModel } from "../models/SoftwareSimpleModel";

@injectable()
export class SoftwareRepository implements ISoftwareRepository {
  async create(
    data: SoftwareCreateRq,
    iconImage?: Blob,
    thumbnail?: Blob,
    config?: AxiosRequestConfig
  ): Promise<Software> {
    const formData = new FormData();
    formData.append(
      "software",
      new Blob([JSON.stringify(data)], { type: "application/json" })
    );
    if (iconImage) formData.append("iconImage", iconImage);
    if (thumbnail) formData.append("thumbnail", thumbnail);
    const result = await API.post<Software>("/api/v1/softwares", formData, {
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": undefined, // FormData 사용 시 Content-Type을 브라우저가 자동 설정하도록 함
      },
    });
    return result.data;
  }

  async update(
    id: string,
    data: SoftwareCreateRq,
    iconImage?: Blob | undefined,
    config?: AxiosRequestConfig
  ): Promise<Software> {
    const formData = new FormData();
    formData.append(
      "software",
      new Blob([JSON.stringify(data)], { type: "application/json" })
    );
    if (iconImage) formData.append("iconImage", iconImage);

    const result = await API.post<Software>(
      `/api/v1/softwares/${id}`,
      formData,
      {
        ...config,
        headers: {
          ...config?.headers,
          "Content-Type": undefined, // FormData 사용 시 Content-Type을 브라우저가 자동 설정하도록 함
        },
      }
    );

    return result.data;
  }

  async getOne(id: string): Promise<Software> {
    const result = await API.get<Software>(`/api/v1/softwares/${id}`);
    return result.data;
  }

  async getList(queries?: Filters): Promise<Page<SoftwareSimpleModel>> {
    const queryString = qs.stringify(queries, { arrayFormat: "repeat" });
    const result = await API.get<Page<SoftwareSimpleModel>>(
      `/api/v1/softwares?${queryString}`
    );
    return result.data;
  }

  async delete(id: string): Promise<void> {
    await API.delete<void>(`/api/v1/softwares/${id}`);
  }

  async getPlanList(id: string): Promise<ProductPlanListDTO[]> {
    const result = await API.get<ProductPlanListDTO[]>(
      `/api/v1/softwares/${id}/product-plans`
    );
    return result.data;
  }

  async getPlanDetail(
    softwareId: string,
    planId: string
  ): Promise<ProductPlan> {
    const result = await API.get<ProductPlan>(
      `/api/v1/softwares/${softwareId}/product-plans/${planId}`
    );
    return result.data;
  }

  async getUsageMetricList(id: string): Promise<UsageMetric[]> {
    const result = await API.get<UsageMetric[]>(
      `/api/v1/softwares/${id}/usage-metrics`
    );
    return result.data;
  }
}
